import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { useMeasure } from 'react-use';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Box } from '@sorare/core/src/atoms/icons/Box';
import { Vault } from '@sorare/core/src/atoms/icons/Vault';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import {
  BrandS,
  HeadlineM,
  LabelL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { ThresholdLabel } from '@sorare/core/src/atoms/ui/SplitProgressBar/ThresholdLabel';
import { Dialog } from '@sorare/core/src/components/dialog';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { formatScarcity } from '@sorare/core/src/lib/cards';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { fantasy, glossary, navLabels } from '@sorare/core/src/lib/glossary';
import { useShardRewardUrl } from '@sorare/core/src/lib/rewards';
import {
  CURRENT_IN_SEASON_FOOTBALL,
  CURRENT_IN_SEASON_USSPORT,
} from '@sorare/core/src/lib/seasons';

import { RarityVaultHowItWorks } from '#src/components/sealing/RarityVaultHowItWorks/index.tsx';
import { SealingGenericAvatar } from '#src/components/sealing/SealingGenericAvatar/index.tsx';
import { VAULT_PROGRESS_BAR_COLORS } from '#src/components/sealing/constant.ts';

import type { RarityVaultHowItWorksDialog_sealingConfig } from './__generated__/index.graphql.ts';
import cashBg from './assets/cash-bg.webp';
import creditBg from './assets/credit-bg.webp';
import essenceBgLimited from './assets/essence-bg-limited.webp';
import essenceBgRare from './assets/essence-bg-rare.webp';
import essenceBgSuperRare from './assets/essence-bg-super_rare.webp';
import essenceBgUnique from './assets/essence-bg-unique.webp';

const Root = styled(Vertical).attrs({ center: true, gap: 4 })`
  padding: var(--triple-unit);

  & > * {
    max-width: calc(46 * var(--unit)); /* 368px */
  }
`;

const StyledSealingGenericAvatar = styled(SealingGenericAvatar)`
  --avatar-size: 100px;
`;

const Row = styled(SBHorizontal)`
  padding: var(--double-unit) 0;
`;

const ThresholdList = styled(Vertical)`
  width: 100%;
  padding: var(--unit) var(--double-unit);

  & > *:not(:last-child) {
    border-bottom: 1px solid var(--c-nd-150);
  }
`;

const BoostsList = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
  padding: var(--triple-unit);
`;

const Boost = styled(Horizontal).attrs({ gap: 1.5 })`
  width: 100%;
  padding: var(--intermediate-unit) var(--double-unit);
  border-radius: var(--double-unit);
  border: 1px solid transparent;
`;

const GradientText = styled(LabelM)`
  background: var(--text-color);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

type Props = {
  open: boolean;
  sealingConfig?: RarityVaultHowItWorksDialog_sealingConfig;
  loading: boolean;
  onClose: () => void;
  rarity: Rarity;
};

const subtitleBySport = defineMessages({
  [Sport.BASEBALL]: {
    defaultMessage:
      'Seal Cards to increase your Vault & Player Loyalty{br}Each level unlocks perks like boosted rewards',
  },
  [Sport.NBA]: {
    defaultMessage:
      'Seal Cards to increase your Vault & Player Loyalty{br}Each level unlocks perks like boosted rewards',
  },
  [Sport.FOOTBALL]: {
    defaultMessage:
      'Sealing cards increases your Vault level,{br}unlocking boosted gameplay rewards.',
  },
});

const getEssenceBackground = (rarity: Rarity) => {
  switch (rarity) {
    case Rarity.rare:
      return `url(${essenceBgRare}) no-repeat center/cover padding-box, linear-gradient(0deg, #800000 0%, #EA000C 100%) border-box`;
    case Rarity.super_rare:
      return `url(${essenceBgSuperRare}) no-repeat center/cover padding-box, linear-gradient(180deg, #179EFF 0%, #0A42D0 100%) border-box`;
    case Rarity.unique:
      return `url(${essenceBgUnique}) no-repeat center/cover padding-box, linear-gradient(0deg, #29073C 0%, #AC11FF 100%) border-box`;
    default:
      return `url(${essenceBgLimited}) no-repeat center/cover padding-box, linear-gradient(0deg, #E67C00 0%, #F7B100 100%) border-box`;
  }
};

export const RarityVaultHowItWorksDialog = ({
  open = true,
  sealingConfig,
  rarity,
  loading,
  onClose,
}: Props) => {
  const { sport } = useSportContext();
  const getShardRewardUrl = useShardRewardUrl();
  const [measurementDiv, { height }] = useMeasure<HTMLDivElement>();
  const [tab, setTab] = useState<'howItWorks' | 'boosts' | 'thresholds'>(
    'howItWorks'
  );

  const [initHeight, setInitHeight] = useState<number | undefined>(undefined);

  if (!initHeight && height > 0) {
    setInitHeight(height);
  }

  const simplifiedMatrix = sealingConfig?.fullRarityVaultMatrix.filter(
    boostLevel => boostLevel.boostBasisPoints > 0
  );

  const boostListConfig = [
    {
      title: <FormattedMessage {...glossary.cashRewards} />,
      image: `${FRONTEND_ASSET_HOST}/rewards/cash.png`,
      background: `url(${cashBg}) no-repeat center/cover padding-box, linear-gradient(82deg, #CC8CD6 -11.67%, #BE92D4 32.51%, #A28BD7 54.72%, #98DDF4 100%) border-box`,
    },
    {
      title: <FormattedMessage defaultMessage="Market Credit Rewards*" />,
      image: `${FRONTEND_ASSET_HOST}/rewards/credit.png`,
      background: `url(${creditBg}) no-repeat center/cover padding-box, linear-gradient(323deg, #f1ff00 -3.1%, #3ddc84 52.7%) border-box`,
    },
    {
      title: <FormattedMessage defaultMessage="Essence Rewards*" />,
      image: getShardRewardUrl(rarity),
      background: getEssenceBackground(rarity),
    },
  ];

  return (
    <Dialog
      maxWidth="xs"
      fullWidth
      open={open}
      onClose={onClose}
      title={
        <Vertical center gap={0}>
          <LabelL bold>
            <FormattedMessage {...navLabels.vault} />
          </LabelL>
          <LabelM color={getRarityColor(rarity)}>
            {formatScarcity(rarity)}
          </LabelM>
        </Vertical>
      }
    >
      {!loading && (
        <div ref={measurementDiv} style={{ height: initHeight }}>
          <TabBar value="howItWorks" variant="flat" forceMobileLayout fullWidth>
            <Tab value="howItWorks" onClick={() => setTab('howItWorks')}>
              <FormattedMessage {...glossary.howItWorks} />
            </Tab>
            <Tab value="boosts" onClick={() => setTab('boosts')}>
              <FormattedMessage defaultMessage="Boosts" />
            </Tab>
            <Tab value="thresholds" onClick={() => setTab('thresholds')}>
              <FormattedMessage defaultMessage="Levels" />
            </Tab>
          </TabBar>
          {tab === 'howItWorks' && (
            <Root>
              <Vertical center gap={2} className="text-center">
                <StyledSealingGenericAvatar />
                <Vertical>
                  <HeadlineM color="var(--c-white)">
                    <FormattedMessage defaultMessage="Sealing Cards" />
                  </HeadlineM>
                  <LabelL color="var(--c-nd-600)">
                    <FormattedMessage
                      {...subtitleBySport[sport]}
                      values={{ br: <br /> }}
                    />
                  </LabelL>
                </Vertical>
              </Vertical>
              <RarityVaultHowItWorks sealingConfig={sealingConfig} />
            </Root>
          )}
          {tab === 'boosts' && (
            <BoostsList center>
              {boostListConfig.map((boostConfig, index) => (
                <Boost
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  style={{ background: boostConfig.background }}
                >
                  <img src={boostConfig.image} alt="" width={32} height={32} />
                  <Vertical gap={0}>
                    <GradientText
                      as={LabelS}
                      style={
                        {
                          '--text-color':
                            'linear-gradient(323deg, #F1FF00 -3.1%, #3DDC84 52.7%)',
                        } as CSSProperties
                      }
                    >
                      <FormattedMessage defaultMessage="Boosted" />
                    </GradientText>
                    <LabelM bold>{boostConfig.title}</LabelM>
                  </Vertical>
                </Boost>
              ))}
              <LabelM color="var(--c-nd-600)">
                <FormattedMessage
                  defaultMessage="*Includes leaderboard rewards from All Seasons and {inSeasonLabel}"
                  values={{
                    inSeasonLabel:
                      sport === Sport.FOOTBALL
                        ? CURRENT_IN_SEASON_FOOTBALL
                        : CURRENT_IN_SEASON_USSPORT,
                  }}
                />
              </LabelM>
            </BoostsList>
          )}
          {tab === 'thresholds' && (
            <ThresholdList
              gap={0}
              style={
                {
                  '--text-color': VAULT_PROGRESS_BAR_COLORS[rarity],
                } as CSSProperties
              }
            >
              {(simplifiedMatrix || []).map(boostLevel => (
                <Row key={boostLevel.points}>
                  <Vertical>
                    <Horizontal gap={0.5}>
                      <BrandS>
                        <FormattedMessage {...navLabels.vault} />
                      </BrandS>
                      <BrandS color={getRarityColor(rarity)}>
                        <FormattedMessage
                          {...fantasy.levelNumber}
                          values={{ level: boostLevel.milestone }}
                        />
                      </BrandS>
                    </Horizontal>
                    <Horizontal>
                      <Vault color="var(--c-rewards-squad)" size={1.5} />
                      <LabelM bold>
                        <FormattedMessage
                          {...fantasy.pointsWithoutFormat}
                          values={{ score: boostLevel.points }}
                        />
                      </LabelM>
                    </Horizontal>
                  </Vertical>
                  <ThresholdLabel
                    bonus={boostLevel.boostBasisPoints / 10000}
                    shiny
                    color="green"
                    icon={<Box size={1.5} />}
                    row
                  />
                </Row>
              ))}
            </ThresholdList>
          )}
        </div>
      )}
    </Dialog>
  );
};

RarityVaultHowItWorksDialog.fragments = {
  sealingConfig: gql`
    fragment RarityVaultHowItWorksDialog_sealingConfig on SealingConfig {
      ...RarityVaultHowItWorks_sealingConfig
      fullRarityVaultMatrix(rarity: $rarity, sport: $sport) {
        boostBasisPoints
        points
        milestone
      }
    }
    ${RarityVaultHowItWorks.fragments.sealingConfig}
  ` as TypedDocumentNode<RarityVaultHowItWorksDialog_sealingConfig>,
};
